import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import type { RealtimeChannel } from '@supabase/supabase-js';
// Import the new types
import type { KeywordCategorization, KeywordCluster } from '@/shared/types/keyword-types';
import { debugLog, debugError, debugWarn } from '@/lib/debugUtils';

/**
 * AI Analysis data types for realtime updates with enhanced interfaces
 */
export interface AiAnalysisPollingData {
  keywordCategorization: KeywordCategorization[];
  contentBriefs: any[];
  keywordClusters: KeywordCluster[];
  funnelStages: any[];
}

/**
 * Analysis status tracking
 */
export interface AnalysisStatus {
  isRunning: boolean;
  startedAt?: Date;
  estimatedCompletion?: Date;
  currentPhase?: 'initializing' | 'processing' | 'analyzing' | 'finalizing' | 'completed';
  progress?: number; // 0-100
}

/**
 * Hook options
 */
export interface UseAiAnalysisDataOptions {
  workspaceId: string;
  pollingInterval?: number; // Kept for compatibility but not used
  analysisTimeoutMinutes?: number; // Kept for compatibility
  onAnalysisComplete?: (data: AiAnalysisPollingData) => void;
  onAnalysisTimeout?: () => void; // Kept for compatibility
  enablePolling?: boolean; // Kept for compatibility but not used
}

/**
 * Hook return interface
 */
export interface UseAiAnalysisDataReturn {
  data: AiAnalysisPollingData;
  analysisStatus: AnalysisStatus;
  isLoading: boolean;
  error: string | null;
  lastUpdated: Date | null;
  startAnalysis: () => Promise<void>;
  stopAnalysis: () => void;
  refreshData: () => Promise<void>;
  timeRemaining: number | null; // Kept for compatibility
}

/**
 * Validates if a user ID is a valid Supabase UUID
 * @param userId - The user ID to validate
 * @returns true if valid UUID, false otherwise
 */
const isValidSupabaseUserId = (userId: string): boolean => {
  // Check if it's a valid UUID format (8-4-4-4-12 hexadecimal characters)
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  return uuidRegex.test(userId);
};

/**
 * Checks if a user ID looks like a Clerk user ID
 * @param userId - The user ID to check
 * @returns true if it looks like a Clerk ID
 */
const isClerkUserId = (userId: string): boolean => {
  return userId.startsWith('user_');
};

/**
 * Enhanced AI Analysis Data Realtime Hook
 * 
 * Features:
 * - Real-time subscriptions to all AI analysis tables
 * - Instant updates when AI workflows complete
 * - Simplified status tracking
 * - Same interface as polling version for component compatibility
 * - Automatic cleanup of subscriptions
 * - Direct user_id filtering for robust authentication
 * - Enhanced validation and error handling
 * 
 * @param options Configuration options
 * @returns AI analysis data, status, and control functions
 */
export function useAiAnalysisData(options: UseAiAnalysisDataOptions): UseAiAnalysisDataReturn {
  const {
    workspaceId,
    onAnalysisComplete,
    analysisTimeoutMinutes = 15
  } = options;

  const { userId, isAuthenticated, user } = useAuth();
  const userEmail = user?.primaryEmailAddress?.emailAddress || null;

  // State management
  const [data, setData] = useState<AiAnalysisPollingData>({
    keywordCategorization: [],
    contentBriefs: [],
    keywordClusters: [],
    funnelStages: []
  });

  const [analysisStatus, setAnalysisStatus] = useState<AnalysisStatus>({
    isRunning: false,
    currentPhase: 'completed',
    progress: 0
  });

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  // Refs for cleanup and status tracking
  const channelsRef = useRef<RealtimeChannel[]>([]);
  const mountedRef = useRef(true);
  const analysisStartTimeRef = useRef<Date | null>(null);

  /**
   * Safely fetch from a table, handling 404 errors gracefully
   * Enhanced with user validation, direct filtering, and workspace context validation
   */
  const safeTableFetch = useCallback(async (tableName: string): Promise<{ data: any[], error: string | null }> => {
    try {
      // Enhanced validation
      if (!userId) {
        debugError(userEmail, `[useAiAnalysisData] No user ID provided for ${tableName} query`);
        return { data: [], error: 'Authentication required' };
      }

      if (isClerkUserId(userId)) {
        debugError(userEmail, `[useAiAnalysisData] Clerk user ID detected for ${tableName}:`, userId);
        debugError(userEmail, `[useAiAnalysisData] This hook requires a Supabase UUID. Check AuthContext mapping.`);
        return { data: [], error: 'Invalid user ID format - authentication mapping issue' };
      }

      if (!isValidSupabaseUserId(userId)) {
        debugError(userEmail, `[useAiAnalysisData] Invalid UUID format for ${tableName}:`, userId);
        return { data: [], error: 'Invalid user ID format' };
      }

      if (!workspaceId) {
        debugError(userEmail, `[useAiAnalysisData] No workspace ID provided for ${tableName} query`);
        return { data: [], error: 'Workspace ID required' };
      }

      debugLog(userEmail, `[useAiAnalysisData] Querying ${tableName} with userId: ${userId}, workspaceId: ${workspaceId}`);
      
      // ENHANCED: Query with workspace context validation to prevent data contamination
      const { data, error } = await supabase
        .from(tableName)
        .select('*')
        .eq('workspace_id', workspaceId)
        .eq('user_id', userId) // DIRECT filtering - no JWT dependency
        .order('created_at', { ascending: false })
        .limit(50);

      if (error) {
        // If table doesn't exist (404), return empty array instead of throwing
        if (error.message?.includes('does not exist') || error.message?.includes('404')) {
          debugLog(userEmail, `[useAiAnalysisData] Table '${tableName}' does not exist yet - returning empty array`);
          return { data: [], error: null };
        }
        // ✅ IMPROVED: Don't hide real errors - only hide table existence errors
        debugWarn(userEmail, `[useAiAnalysisData] Error fetching from ${tableName}:`, error.message);
        return { data: [], error: `Failed to fetch ${tableName}: ${error.message}` };
      }

      // ENHANCED: Validate workspace context to detect data contamination with content analysis
      if (data && data.length > 0) {
        const workspaceMismatch = data.filter(item => item.workspace_id !== workspaceId);
        if (workspaceMismatch.length > 0) {
          debugError(userEmail, `[useAiAnalysisData] WORKSPACE CONTAMINATION DETECTED in ${tableName}:`, {
            expectedWorkspaceId: workspaceId,
            contaminatedRecords: workspaceMismatch.length,
            sampleMismatch: workspaceMismatch[0]
          });
          // Filter out contaminated data
          const cleanData = data.filter(item => item.workspace_id === workspaceId);
          debugLog(userEmail, `[useAiAnalysisData] Filtered contaminated data - ${cleanData.length}/${data.length} records remain`);
          return { data: cleanData, error: null };
        }

        // ADDITIONAL: Content-based contamination detection for AI analysis tables
        if (tableName === 'keyword_categorization' || tableName === 'content_briefs') {
          const contaminatedByContent = data.filter(item => {
            // Check for obvious content mismatches based on keywords or content
            const contentText = (item.keyword_text || item.title || item.content || '').toLowerCase();
            const workspaceName = workspaceId.toLowerCase();
            
            // Define contamination patterns
            const weightLossTerms = ['weight loss', 'diet', 'fitness', 'fat loss', 'slim', 'obesity', 'calories', 'exercise'];
            const jewelryTerms = ['jewelry', 'jewellery', 'terracotta', 'ornament', 'accessory', 'necklace', 'earring'];
            
            // If this is terracotta jewelry workspace but content contains weight loss terms
            if (workspaceName.includes('terracotta') && weightLossTerms.some(term => contentText.includes(term))) {
              return true;
            }
            
            // If this is weight loss workspace but content contains jewelry terms
            if ((workspaceName.includes('weight') || workspaceName.includes('diet')) && 
                jewelryTerms.some(term => contentText.includes(term))) {
              return true;
            }
            
            return false;
          });
          
          if (contaminatedByContent.length > 0) {
            debugError(userEmail, `[useAiAnalysisData] CONTENT-BASED CONTAMINATION DETECTED in ${tableName}:`, {
              workspaceId,
              contaminatedByContent: contaminatedByContent.length,
              sampleContaminatedContent: contaminatedByContent[0],
              contaminatedKeywords: contaminatedByContent.map(item => item.keyword_text || item.title).slice(0, 5)
            });
            
            // Filter out content-contaminated data
            const cleanData = data.filter(item => !contaminatedByContent.includes(item));
            debugLog(userEmail, `[useAiAnalysisData] Filtered content-contaminated data - ${cleanData.length}/${data.length} records remain`);
            return { data: cleanData, error: null };
          }
        }
      }

      debugLog(userEmail, `[useAiAnalysisData] Query results for ${tableName}:`, data?.length || 0, 'records');
      return { data: data || [], error: null };
    } catch (err: any) {
      // Handle 404 and other table-not-found errors gracefully
      if (err.message?.includes('does not exist') || err.message?.includes('404') || err.code === 'PGRST116') {
        debugLog(userEmail, `[useAiAnalysisData] Table '${tableName}' not found - this is expected until AI analysis creates the data`);
        return { data: [], error: null };
      }
      // ✅ IMPROVED: Expose real errors instead of hiding them
      debugWarn(userEmail, `[useAiAnalysisData] Error processing ${tableName}:`, err.message);
      return { data: [], error: `Error processing ${tableName}: ${err.message}` };
    }
  }, [workspaceId, userId]);

  /**
   * Enhanced fetch for keyword categorization that includes search volume
   * Uses keyword text matching to get volume data and falls back to denormalized fields
   * UPDATED: Uses new KeywordCategorization interface and includes content_type field
   */
  const fetchKeywordCategorizationWithMetrics = useCallback(async (): Promise<{ data: KeywordCategorization[], error: string | null }> => {
    try {
      // Enhanced validation
      if (!userId || !workspaceId) {
        debugWarn(userEmail, '[useAiAnalysisData] Missing userId or workspaceId for keyword categorization with metrics');
        return { data: [], error: 'Missing required parameters' };
      }

      if (isClerkUserId(userId)) {
        debugError(userEmail, '[useAiAnalysisData] Clerk user ID detected for keyword categorization with metrics:', userId);
        return { data: [], error: 'Invalid user ID format - authentication mapping issue' };
      }

      if (!isValidSupabaseUserId(userId)) {
        debugError(userEmail, '[useAiAnalysisData] Invalid UUID format for keyword categorization with metrics:', userId);
        return { data: [], error: 'Invalid user ID format' };
      }

      debugLog(userEmail, '[useAiAnalysisData] Fetching keyword categorization with metrics');

      // First, get the categorization data using the simple fetch (which we know works)
      const categorizationResult = await safeTableFetch('keyword_categorization');
      
      if (categorizationResult.error || !categorizationResult.data || categorizationResult.data.length === 0) {
        debugLog(userEmail, '[useAiAnalysisData] No categorization data found');
        return categorizationResult as { data: KeywordCategorization[], error: string | null };
      }

      // Extract unique keywords to look up search volumes
      const keywordTexts = [...new Set(categorizationResult.data.map(item => item.keyword_text))];
      
      debugLog(userEmail, '[useAiAnalysisData] Looking up search volumes for', keywordTexts.length, 'keywords');

      // Get search volumes for these keywords
      const { data: volumeData, error: volumeError } = await supabase
        .from('keyword_consolidated_data')
        .select('keyword, search_volume')
        .eq('workspace_id', workspaceId)
        .eq('user_id', userId)
        .in('keyword', keywordTexts)
        .not('search_volume', 'is', null);

      if (volumeError) {
        debugWarn(userEmail, '[useAiAnalysisData] Error fetching search volumes:', volumeError.message);
      }

      // Create a lookup map for search volumes
      const volumeLookup = new Map();
      if (volumeData) {
        volumeData.forEach(item => {
          // Use the highest non-null search volume for each keyword
          if (item.search_volume && (!volumeLookup.has(item.keyword) || volumeLookup.get(item.keyword) < item.search_volume)) {
            volumeLookup.set(item.keyword, item.search_volume);
          }
        });
      }

      // ENHANCED: Categorization data with proper field preservation and new content_type field
      const enhancedData: KeywordCategorization[] = categorizationResult.data.map(item => ({
        ...item, // First spread all original fields to preserve everything
        // Add search volume from lookup
        search_volume: volumeLookup.get(item.keyword_text) || null,
        msv: volumeLookup.get(item.keyword_text) || null,
        // Explicitly preserve critical fields to ensure they're not overwritten
        category_reasoning: item.category_reasoning,
        kw_difficulty: item.kw_difficulty, 
        competition: item.competition,
        // Keep other existing denormalized fields
        difficulty: item.kw_difficulty,
        cpc: item.cpc,
        // NEW: content_type field support
        content_type: item.content_type || null
      }));

      debugLog(userEmail, '[useAiAnalysisData] Keyword categorization with metrics enhanced:', enhancedData.length, 'records');
      debugLog(userEmail, '[useAiAnalysisData] Sample enhanced record with all fields:', enhancedData[0]);
      debugLog(userEmail, '[useAiAnalysisData] Fields check:', {
        has_category_reasoning: !!enhancedData[0]?.category_reasoning,
        has_kw_difficulty: !!enhancedData[0]?.kw_difficulty,
        has_competition: !!enhancedData[0]?.competition,
        has_content_type: !!enhancedData[0]?.content_type
      });
      
      return { data: enhancedData, error: null };
    } catch (err: any) {
      debugWarn(userEmail, '[useAiAnalysisData] Error processing keyword categorization with metrics:', err.message);
      // Fall back to basic categorization data if enhancement fails
      return await safeTableFetch('keyword_categorization') as { data: KeywordCategorization[], error: string | null };
    }
  }, [workspaceId, userId, safeTableFetch]);

  /**
   * Fetch funnel stages from keyword_consolidated_data
   * Enhanced with direct user_id filtering
   */
  const fetchFunnelStages = useCallback(async (): Promise<{ data: any[], error: string | null }> => {
    try {
      // Enhanced validation
      if (!userId || !workspaceId) {
        debugWarn(userEmail, '[useAiAnalysisData] Missing userId or workspaceId for funnel stages query');
        return { data: [], error: 'Missing required parameters' };
      }

      if (isClerkUserId(userId)) {
        debugError(userEmail, '[useAiAnalysisData] Clerk user ID detected for funnel stages:', userId);
        return { data: [], error: 'Invalid user ID format - authentication mapping issue' };
      }

      if (!isValidSupabaseUserId(userId)) {
        debugError(userEmail, '[useAiAnalysisData] Invalid UUID format for funnel stages:', userId);
        return { data: [], error: 'Invalid user ID format' };
      }

      // CRITICAL: Direct user_id filtering
      const { data, error } = await supabase
        .from('keyword_consolidated_data')
        .select('funnel_stage')
        .eq('workspace_id', workspaceId)
        .eq('user_id', userId) // DIRECT filtering
        .not('funnel_stage', 'is', null);

      if (error) {
        debugWarn(userEmail, '[useAiAnalysisData] Error fetching funnel stages:', error.message);
        return { data: [], error: `Failed to fetch funnel stages: ${error.message}` };
      }

      // Get unique funnel stages
      const uniqueStages = [...new Set((data || []).map(item => item.funnel_stage))];
      return { data: uniqueStages.map(stage => ({ stage, count: data?.filter(item => item.funnel_stage === stage).length || 0 })), error: null };
    } catch (err: any) {
      debugWarn(userEmail, '[useAiAnalysisData] Error processing funnel stages:', err.message);
      return { data: [], error: `Error processing funnel stages: ${err.message}` };
    }
  }, [workspaceId, userId]);

  /**
   * Fetch keyword clusters with enhanced fields
   * UPDATED: Includes new fields from schema migration, uses KeywordCluster interface, and validates workspace context
   */
  const fetchKeywordClusters = useCallback(async (): Promise<{ data: KeywordCluster[], error: string | null }> => {
    try {
      // Enhanced validation
      if (!userId || !workspaceId) {
        debugWarn(userEmail, '[useAiAnalysisData] Missing userId or workspaceId for keyword clusters');
        return { data: [], error: 'Missing required parameters' };
      }

      if (isClerkUserId(userId)) {
        debugError(userEmail, '[useAiAnalysisData] Clerk user ID detected for keyword clusters:', userId);
        return { data: [], error: 'Invalid user ID format - authentication mapping issue' };
      }

      if (!isValidSupabaseUserId(userId)) {
        debugError(userEmail, '[useAiAnalysisData] Invalid UUID format for keyword clusters:', userId);
        return { data: [], error: 'Invalid user ID format' };
      }

      debugLog(userEmail, '[useAiAnalysisData] Fetching keyword clusters with enhanced fields');

      // UPDATED: Select all fields including new enhanced fields (only existing columns)
      const { data, error } = await supabase
        .from('keyword_clusters')
        .select('*')
        .eq('workspace_id', workspaceId)
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(50);

      if (error) {
        debugWarn(userEmail, '[useAiAnalysisData] Error fetching keyword clusters:', error.message);
        return { data: [], error: `Failed to fetch keyword clusters: ${error.message}` };
      }

      // ENHANCED: Validate workspace context to detect and filter contaminated data
      if (data && data.length > 0) {
        const workspaceMismatch = data.filter(item => item.workspace_id !== workspaceId);
        if (workspaceMismatch.length > 0) {
          debugError(userEmail, '[useAiAnalysisData] WORKSPACE CONTAMINATION DETECTED in keyword_clusters:', {
            expectedWorkspaceId: workspaceId,
            contaminatedRecords: workspaceMismatch.length,
            sampleContamination: {
              found_workspace_id: workspaceMismatch[0].workspace_id,
              cluster_name: workspaceMismatch[0].cluster_name,
              primary_keyword: workspaceMismatch[0].primary_keyword
            }
          });
          // Filter out contaminated data for immediate relief
          const cleanData = data.filter(item => item.workspace_id === workspaceId);
          debugLog(userEmail, '[useAiAnalysisData] Filtered contaminated clusters - ' + 
                   `${cleanData.length}/${data.length} records remain`);
          return { data: cleanData as KeywordCluster[], error: null };
        }
      }

      debugLog(userEmail, '[useAiAnalysisData] Keyword clusters fetched:', data?.length || 0, 'records');
      debugLog(userEmail, '[useAiAnalysisData] Sample cluster with enhanced fields:', data?.[0]);

      return { data: (data || []) as KeywordCluster[], error: null };
    } catch (err: any) {
      debugWarn(userEmail, '[useAiAnalysisData] Error processing keyword clusters:', err.message);
      return { data: [], error: `Error processing keyword clusters: ${err.message}` };
    }
  }, [workspaceId, userId]);

  /**
   * Fetch AI analysis data from all relevant tables
   * Enhanced with validation and direct filtering
   */
  const fetchAnalysisData = useCallback(async (): Promise<AiAnalysisPollingData> => {
    if (!userId || !isAuthenticated) {
      throw new Error('User not authenticated');
    }

    // Enhanced user validation
    if (isClerkUserId(userId)) {
      throw new Error(`Invalid user ID format: Clerk user IDs are not supported for direct database queries. Received: ${userId}. Expected Supabase UUID format.`);
    }

    if (!isValidSupabaseUserId(userId)) {
      throw new Error(`Invalid user ID format: Must be a valid UUID. Received: ${userId}. Expected UUID format: xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx`);
    }

    debugLog(userEmail, '[useAiAnalysisData] Fetching analysis data for workspace:', workspaceId);
    debugLog(userEmail, '[useAiAnalysisData] Using userId:', userId);

    // Fetch data from all AI analysis tables in parallel
    const [
      keywordCategorizationResult,
      contentBriefsResult,
      keywordClustersResult,
      funnelStagesResult
    ] = await Promise.all([
      fetchKeywordCategorizationWithMetrics(), // Use enhanced fetch with metrics
      safeTableFetch('content_briefs'),
      fetchKeywordClusters(), // Use enhanced fetch with new fields
      fetchFunnelStages()
    ]);

    // ✅ IMPROVED: Collect and report all errors instead of hiding them
    const errors = [
      keywordCategorizationResult.error,
      contentBriefsResult.error,
      keywordClustersResult.error,
      funnelStagesResult.error
    ].filter(Boolean);

    if (errors.length > 0) {
      debugWarn(userEmail, '[useAiAnalysisData] Some errors occurred during data fetch:', errors);
      // Don't throw - just log the errors and continue with available data
    }

    return {
      keywordCategorization: keywordCategorizationResult.data,
      contentBriefs: contentBriefsResult.data,
      keywordClusters: keywordClustersResult.data,
      funnelStages: funnelStagesResult.data
    };
  }, [userId, isAuthenticated, workspaceId, fetchKeywordCategorizationWithMetrics, safeTableFetch, fetchKeywordClusters, fetchFunnelStages]);

  /**
   * Update analysis status based on data availability
   */
  const updateAnalysisStatus = useCallback((fetchedData: AiAnalysisPollingData) => {
    const hasData = 
      fetchedData.keywordCategorization.length > 0 ||
      fetchedData.contentBriefs.length > 0 ||
      fetchedData.keywordClusters.length > 0 ||
      fetchedData.funnelStages.length > 0;

    // Calculate progress based on data availability
    const dataProgress = [
      fetchedData.keywordCategorization.length > 0 ? 25 : 0,
      fetchedData.contentBriefs.length > 0 ? 25 : 0,
      fetchedData.keywordClusters.length > 0 ? 25 : 0,
      fetchedData.funnelStages.length > 0 ? 25 : 0
    ].reduce((sum, val) => sum + val, 0);

    setAnalysisStatus(prev => {
      // If we previously had no data and now we do, analysis is likely complete
      if (!prev.isRunning && hasData) {
        return {
          ...prev,
          currentPhase: 'completed',
          progress: 100
        };
      }
      
      // If analysis is running, update progress
      if (prev.isRunning) {
        return {
          ...prev,
          progress: dataProgress,
          currentPhase: dataProgress === 100 ? 'completed' : 'processing'
        };
      }

      return prev;
    });

    // Trigger completion callback if analysis was running and now has substantial data
    if (analysisStatus.isRunning && dataProgress >= 75 && onAnalysisComplete) {
      onAnalysisComplete(fetchedData);
    }
  }, [analysisStatus.isRunning, onAnalysisComplete]);

  /**
   * Refresh data manually
   */
  const refreshData = useCallback(async () => {
    if (!mountedRef.current) return;

    try {
      setIsLoading(true);
      setError(null);

      const fetchedData = await fetchAnalysisData();
      
      if (!mountedRef.current) return;

      setData(fetchedData);
      setLastUpdated(new Date());
      updateAnalysisStatus(fetchedData);

      debugLog(userEmail, '[useAiAnalysisData] Data refreshed successfully');

    } catch (err: any) {
      debugError(userEmail, '[useAiAnalysisData] Error refreshing data:', err);
      if (mountedRef.current) {
        // ✅ IMPROVED: Show actual error messages instead of generic ones
        setError(err.message || 'Failed to refresh analysis data');
      }
    } finally {
      if (mountedRef.current) {
        setIsLoading(false);
      }
    }
  }, [fetchAnalysisData, updateAnalysisStatus]);

  /**
   * Set up realtime subscriptions for all AI analysis tables
   */
  const setupRealtimeSubscriptions = useCallback(() => {
    if (!userId || !workspaceId) return;

    debugLog(userEmail, '[useAiAnalysisData] Setting up realtime subscriptions');

    const tableNames = ['keyword_categorization', 'content_briefs', 'keyword_clusters', 'keyword_consolidated_data'];
    
    tableNames.forEach(tableName => {
      const channel = supabase
        .channel(`ai_analysis_${tableName}_${workspaceId}`)
        .on(
          'postgres_changes',
          {
            event: '*', // Listen for INSERT, UPDATE, DELETE
            schema: 'public',
            table: tableName,
            filter: `workspace_id=eq.${workspaceId}`
          },
          (payload) => {
            debugLog(userEmail, `[useAiAnalysisData] Realtime update from ${tableName}:`, payload.eventType);
            
            // Verify the change is for this user
            if (payload.new && (payload.new as any).user_id === userId) {
              // Refresh data when we get updates
              refreshData();
            }
          }
        )
        .subscribe((status) => {
          debugLog(userEmail, `[useAiAnalysisData] Subscription status for ${tableName}:`, status);
        });

      channelsRef.current.push(channel);
    });
  }, [userId, workspaceId, refreshData]);

  /**
   * Clean up realtime subscriptions
   */
  const cleanupSubscriptions = useCallback(() => {
    debugLog(userEmail, '[useAiAnalysisData] Cleaning up realtime subscriptions');
    
    channelsRef.current.forEach(channel => {
      supabase.removeChannel(channel);
    });
    
    channelsRef.current = [];
  }, []);

  /**
   * Start AI analysis tracking
   */
  const startAnalysis = useCallback(async () => {
    if (!userId || !workspaceId) {
      setError('Missing user ID or workspace ID');
      return;
    }

    const startTime = new Date();
    analysisStartTimeRef.current = startTime;

    setAnalysisStatus({
      isRunning: true,
      startedAt: startTime,
      estimatedCompletion: new Date(startTime.getTime() + (analysisTimeoutMinutes * 60 * 1000)),
      currentPhase: 'initializing',
      progress: 0
    });

    // Show user notification about analysis
    toast.info('AI Analysis Started', {
      description: `Analysis will take approximately ${analysisTimeoutMinutes} minutes to complete. You'll get real-time updates.`,
      duration: 8000
    });

    debugLog(userEmail, '[useAiAnalysisData] Analysis started, realtime subscriptions active');
  }, [userId, workspaceId, analysisTimeoutMinutes]);

  /**
   * Stop analysis
   */
  const stopAnalysis = useCallback(() => {
    setAnalysisStatus(prev => ({
      ...prev,
      isRunning: false,
      currentPhase: 'completed'
    }));
    
    analysisStartTimeRef.current = null;
    debugLog(userEmail, '[useAiAnalysisData] Analysis stopped');
  }, []);

  /**
   * Set up realtime subscriptions on mount and when dependencies change
   * FIXED: Removed function dependencies to prevent infinite loop
   */
  useEffect(() => {
    if (userId && workspaceId) {
      setupRealtimeSubscriptions();
      refreshData(); // Initial data fetch
    }

    return cleanupSubscriptions;
  }, [userId, workspaceId]); // Only depend on primitive values, not functions

  /**
   * Cleanup on unmount
   */
  useEffect(() => {
    mountedRef.current = true;
    
    return () => {
      mountedRef.current = false;
      cleanupSubscriptions();
    };
  }, [cleanupSubscriptions]);

  return {
    data,
    analysisStatus,
    isLoading,
    error,
    lastUpdated,
    startAnalysis,
    stopAnalysis,
    refreshData,
    timeRemaining: null // Simplified - realtime doesn't need countdown timers
  };
}